import { FullProject, ProjectMetadata, Task, LogEntry } from '../../../types';

const fmtDate = (dStr?: string) => {
  if (!dStr) return '';
  try {
    const parts = dStr.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return dStr;
  } catch {
    return dStr;
  }
};

export async function runExportAsExcel(
  activeProject: FullProject | undefined,
  tasks: Task[],
  projectMetadata: ProjectMetadata,
  addLogEntry: (action: LogEntry['action'], description: string) => void
) {
  const XLSX = await import('xlsx');
  const currentProject = activeProject;
  const pName = currentProject?.name || 'Unbekanntes_Projekt';

  const metadataRows = [
    ['Projektname', pName],
    ['Status', tasks.length > 0 ? (tasks.every(t => t.completed) ? 'Abgeschlossen' : 'In Bearbeitung') : 'Leer'],
    ['Workspace', currentProject?.workspace || 'professional'],
    ['', ''],
    ['STRATEGISCHE BEWERTUNG', ''],
    ['Impact Score', projectMetadata.impactScore || 0],
    ['Effort Score', projectMetadata.effortScore || 0],
    ['Priorität (Gesamt)', projectMetadata.priority || 'Normal'],
    ['', ''],
    ['PROJEKT-KONZEPT & METADATEN', ''],
    ['Vision', projectMetadata.vision || ''],
    ['Ziele', projectMetadata.goals || ''],
    ['Stakeholder', Array.isArray(projectMetadata.stakeholders) ? JSON.stringify(projectMetadata.stakeholders) : projectMetadata.stakeholders || ''],
    ['Theory of Change', projectMetadata.theoryOfChange || ''],
    ['Partizipation', projectMetadata.participation || ''],
    ['Ressourcen', projectMetadata.resources || ''],
    ['Budget', projectMetadata.budget || ''],
    ['Risiken', projectMetadata.risks || ''],
    ['Strategische Beschreibung', projectMetadata.description || ''],
    ['Kommunikation', projectMetadata.communication || ''],
    ['Nachhaltigkeit', projectMetadata.sustainability || ''],
    ['KPIs', projectMetadata.kpis || ''],
    ['', ''],
    ['ANALYSE-ERGEBNISSE', ''],
    ['SWOT-Analyse', projectMetadata.swot ? 'Vorhanden' : 'Nicht definiert'],
    ['RACI-Matrix', projectMetadata.raci ? 'Vorhanden' : 'Nicht definiert'],
    ['Risiko-Matrix', projectMetadata.riskMatrix ? 'Vorhanden' : 'Nicht definiert'],
  ];

  const metaSheet = XLSX.utils.aoa_to_sheet(metadataRows);

  const taskHeaders = [
    'ID', 'Vorgang', 'Typ', 'Kategorie', 'Priorität', 'Status',
    'Start', 'Ende', 'Uhrzeit Start', 'Uhrzeit Ende',
    'Zuständig', 'Organisation', 'Rolle', 'E-Mail', 'Telefon',
    'Abhängigkeiten', 'Unteraufgabe von', 'Checklisten-Fortschritt', 'Beschreibung',
  ];

  const taskRows = tasks.map(t => {
    const checklistStr =
      t.contactChecklist && t.contactChecklist.length > 0
        ? `${t.contactChecklist.filter(c => c.completed).length}/${t.contactChecklist.length}`
        : '';

    return [
      t.id,
      t.name,
      t.type || 'task',
      t.category || '',
      t.priority || 'none',
      t.completed ? 'Erledigt' : t.status || 'Offen',
      fmtDate(t.start),
      fmtDate(t.end),
      t.startTime || '',
      t.endTime || '',
      t.assignee || '',
      t.organization || '',
      t.role || '',
      t.contactEmail || '',
      t.contactPhone || '',
      (t.dependencies || []).join(', '),
      t.parentId || '',
      checklistStr,
      Array.isArray(t.notes) ? t.notes.map(n => n.text).join(' | ') : t.notes || '',
    ];
  });

  const taskSheet = XLSX.utils.aoa_to_sheet([taskHeaders, ...taskRows]);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, metaSheet, 'Projekt-Steckbrief');
  XLSX.utils.book_append_sheet(wb, taskSheet, 'Aufgaben & Termine');

  XLSX.writeFile(wb, `${pName.replace(/\s+/g, '_')}_Export.xlsx`);
  addLogEntry('import', `Projekt "${pName}" vollständig als Excel exportiert`);
}

export async function runExportTaskTableAsExcel(
  activeProject: FullProject | undefined,
  tasks: Task[],
  allProjects: FullProject[]
) {
  const XLSX = await import('xlsx');
  const pName = activeProject?.name || 'Projekt';
  const projectTasks = tasks || [];

  const taskMap = new Map<string | number, string>();
  allProjects.forEach(p => (p.tasks || []).forEach(t => taskMap.set(t.id, t.name)));

  const sortedTasks = [...projectTasks].sort((a, b) => {
    const timeA = new Date(a.start || 0).getTime();
    const timeB = new Date(b.start || 0).getTime();
    return timeA - timeB;
  });

  const headers = [
    'Vorgang/Aufgabe', 'Zeitraum Start', 'Zeitraum Ende', 'Uhrzeit',
    'Zuständigkeit', 'Typ', 'Priorität', 'Status',
    'Abhängigkeiten', 'Unteraufgabe von', 'Unteraufgaben', 'Beschreibung',
  ];

  const data = sortedTasks.map(t => {
    const subtasks = projectTasks.filter(st => String(st.parentId) === String(t.id));
    return [
      t.name,
      fmtDate(t.start),
      fmtDate(t.end),
      t.startTime ? `${t.startTime} - ${t.endTime || ''}` : '',
      t.assignee || '',
      t.type || 'task',
      t.priority || 'none',
      t.completed ? 'Erledigt' : t.status || 'Offen',
      (t.dependencies || []).map(depId => taskMap.get(depId) || depId).join(', '),
      t.parentId ? taskMap.get(t.parentId) || t.parentId : '',
      subtasks.map(st => st.name).join(', '),
      Array.isArray(t.notes) ? t.notes.map(n => n.text).join(' | ') : t.notes || '',
    ];
  });

  const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
  ws['!cols'] = [
    { wch: 40 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
    { wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 15 },
    { wch: 30 }, { wch: 25 }, { wch: 40 }, { wch: 50 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Aufgaben');
  XLSX.writeFile(wb, `${pName}_Aufgaben.xlsx`);
}

export async function runExportKanbanColumnAsExcel(
  activeProject: FullProject | undefined,
  tasks: Task[],
  allProjects: FullProject[],
  status: string,
  columnTitle: string,
  isGlobalOrTasks: boolean | Task[] = false,
  columnTasksOverride?: Task[]
) {
  const XLSX = await import('xlsx');
  const isGlobal = typeof isGlobalOrTasks === 'boolean' ? isGlobalOrTasks : false;
  const resolvedTasksOverride = Array.isArray(isGlobalOrTasks) ? isGlobalOrTasks : columnTasksOverride;
  const pName = isGlobal ? 'Global' : activeProject?.name || 'Projekt';

  const sourceTasks =
    resolvedTasksOverride ||
    (isGlobal
      ? allProjects.flatMap(p => (p.tasks || []).map(t => ({ ...t, projectName: p.name })))
      : tasks.map(t => ({ ...t, projectName: activeProject?.name || '' })));

  const normalizeStatus = (s: string) => {
    const lower = (s || '').toLowerCase();
    if (lower === 'offen' || lower === 'vormerkung' || lower === 'todo') return 'open';
    if (lower === 'in arbeit' || lower === 'pending' || lower === 'in_progress') return 'in_progress';
    if (lower === 'wartend' || lower === 'hold') return 'hold';
    if (lower === 'blockiert') return 'blocked';
    if (lower === 'erledigt' || lower === 'done' || lower === 'partner' || lower === 'completed') return 'completed';
    return lower;
  };

  const targetStatus = normalizeStatus(status);
  const columnTasks =
    resolvedTasksOverride ||
    sourceTasks.filter(t => {
      const tStatus = normalizeStatus(t.status || (t.completed ? 'completed' : 'open'));
      return tStatus === targetStatus || (targetStatus === 'completed' && t.completed);
    });

  if (columnTasks.length === 0) {
    alert('Diese Spalte enthält keine Aufgaben zum Exportieren.');
    return;
  }

  const taskMap = new Map<string | number, string>();
  allProjects.forEach(p => (p.tasks || []).forEach(t => taskMap.set(t.id, t.name)));

  const headers = [
    'Vorgang/Aufgabe', 'Zeitraum Start', 'Zeitraum Ende', 'Uhrzeit',
    'Zuständigkeit', 'Typ', 'Priorität', 'Status',
    'Abhängigkeiten', 'Unteraufgabe von', 'Unteraufgaben', 'Beschreibung',
  ];
  if (isGlobal) headers.unshift('Projekt');

  const data = columnTasks.map(t => {
    const subtasks = sourceTasks.filter(st => String(st.parentId) === String(t.id));
    const row = [
      t.name,
      fmtDate(t.start),
      fmtDate(t.end),
      t.startTime ? `${t.startTime} - ${t.endTime || ''}` : '',
      t.assignee || '',
      t.type || 'task',
      t.priority || 'none',
      t.completed ? 'Erledigt' : t.status || 'Offen',
      (t.dependencies || []).map(depId => taskMap.get(depId) || depId).join(', '),
      t.parentId ? taskMap.get(t.parentId) || t.parentId : '',
      subtasks.map(st => st.name).join(', '),
      Array.isArray(t.notes) ? t.notes.map(n => n.text).join(' | ') : t.notes || '',
    ];
    if (isGlobal) row.unshift(t.projectName || '');
    return row;
  });

  const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
  const wscols = [
    { wch: 40 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
    { wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 15 },
    { wch: 30 }, { wch: 25 }, { wch: 40 }, { wch: 50 },
  ];
  if (isGlobal) wscols.unshift({ wch: 25 });
  ws['!cols'] = wscols;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, columnTitle.substring(0, 31));
  XLSX.writeFile(wb, `${pName}_${columnTitle}_Export.xlsx`);
}

export async function runExportContactsAsExcel(
  activeProject: FullProject | undefined,
  allProjects: FullProject[],
  isGlobal: boolean = false
) {
  const XLSX = await import('xlsx');
  const pName = isGlobal ? 'Globales_Verzeichnis' : activeProject?.name || 'Projekt';

  const sourceTasks = isGlobal
    ? allProjects.flatMap(p => p.tasks || [])
    : activeProject?.tasks || [];

  const contactMap = new Map<string, Task>();
  sourceTasks.forEach(t => {
    if (!t.assignee) return;
    const name = t.assignee.trim();
    if (!name) return;

    const existing = contactMap.get(name);
    if (!existing) {
      contactMap.set(name, { ...t });
    } else {
      const isNewContactType = t.type === 'contact';
      const isExistingContactType = existing.type === 'contact';

      const getScore = (taskItem: Task) =>
        (taskItem.contactEmail ? 1 : 0) +
        (taskItem.contactPhone ? 1 : 0) +
        (taskItem.organization ? 1 : 0) +
        (taskItem.role ? 1 : 0) +
        (taskItem.socialCapital ? 1 : 0) +
        (taskItem.mood ? 1 : 0);

      const newScore = getScore(t);
      const existingScore = getScore(existing);

      const shouldOverwriteIdentity =
        (isNewContactType && !isExistingContactType) ||
        (isNewContactType === isExistingContactType && newScore > existingScore);

      const latestContactDate =
        t.lastContactDate && existing.lastContactDate
          ? t.lastContactDate > existing.lastContactDate
            ? t.lastContactDate
            : existing.lastContactDate
          : t.lastContactDate || existing.lastContactDate;

      if (shouldOverwriteIdentity) {
        const mergedNotes = existing.notes || t.notes;
        contactMap.set(name, {
          ...existing,
          ...t,
          notes: mergedNotes,
          lastContactDate: latestContactDate,
        });
      } else {
        contactMap.set(name, {
          ...t,
          ...existing,
          contactEmail: existing.contactEmail || t.contactEmail,
          contactPhone: existing.contactPhone || t.contactPhone,
          organization: existing.organization || t.organization,
          role: existing.role || t.role,
          socialCapital: existing.socialCapital || t.socialCapital,
          mood: existing.mood || t.mood,
          lastContactDate: latestContactDate,
        });
      }
    }
  });

  const contacts = Array.from(contactMap.values()).sort((a, b) => (a.assignee || '').localeCompare(b.assignee || ''));

  const headers = isGlobal
    ? ['Name', 'Organisation', 'Abteilung/Bereich', 'Rolle/Funktion', 'E-Mail', 'Telefon', 'Letzter Kontakt', 'Notizen (Zusammenfassend)']
    : ['Name', 'Organisation', 'Abteilung/Bereich', 'Rolle/Funktion', 'E-Mail', 'Telefon', 'Social Capital', 'Stimmung', 'Letzter Kontakt', 'Notizen (Zusammenfassend)'];

  const data = contacts.map(c => {
    const personTasks = sourceTasks.filter(t => t.assignee === c.assignee);
    const allNotes = personTasks
      .map(t => (Array.isArray(t.notes) ? t.notes.map(n => n.text).join(' | ') : t.notes || ''))
      .filter(n => n.length > 0)
      .join(' || ');

    const row: (string | number | undefined)[] = [
      c.assignee,
      c.organization || '',
      c.category || '',
      c.role || '',
      c.contactEmail || '',
      c.contactPhone || '',
    ];

    if (!isGlobal) {
      row.push(c.socialCapital || 0);
      row.push(c.mood || '');
    }

    row.push(fmtDate(c.lastContactDate));
    row.push(allNotes);

    return row;
  });

  const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
  ws['!cols'] = [
    { wch: 25 }, { wch: 25 }, { wch: 20 }, { wch: 20 },
    { wch: 30 }, { wch: 20 }, { wch: 15 }, { wch: 15 },
    { wch: 15 }, { wch: 50 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Kontakte');
  XLSX.writeFile(wb, `${pName}_Kontakte.xlsx`);
}
